import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface Documento {
  id: string;
  workspaceId: string;
  titulo: string;
  descricao: string | null;
  r2Key: string;
  mimeType: string;
  tamanhoBytes: number;
  tags: string[];
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentosListResult {
  items: Documento[];
  total: number;
  limit: number;
  offset: number;
}

export interface DocumentosAggregate {
  count: number;
  byType: Record<string, number>;
}

export interface DocumentosFilters {
  tag?: string;
  search?: string;
  mime?: string;
  limit?: number;
  offset?: number;
}

export interface CreateDocumentoInput {
  titulo: string;
  descricao?: string;
  r2Key: string;
  mimeType: string;
  tamanhoBytes: number;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

function basePath(workspaceId: string): string {
  return `/v1/workspaces/${workspaceId}/documentos`;
}

function buildQuery(filters: DocumentosFilters): string {
  const q = new URLSearchParams();
  if (filters.tag) q.set('tag', filters.tag);
  if (filters.search) q.set('search', filters.search);
  if (filters.mime) q.set('mime', filters.mime);
  if (filters.limit != null) q.set('limit', String(filters.limit));
  if (filters.offset != null) q.set('offset', String(filters.offset));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function useDocumentos(workspaceId: string | null | undefined, filters: DocumentosFilters) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['documentos', workspaceId, filters],
    queryFn: () =>
      api.get<DocumentosListResult>(`${basePath(workspaceId!)}/${buildQuery(filters)}`),
  });
}

export function useDocumentosAggregate(workspaceId: string | null | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['documentos', workspaceId, 'aggregate'],
    queryFn: () =>
      api.get<DocumentosAggregate>(`${basePath(workspaceId!)}/aggregate`),
  });
}

export function useCreateDocumento(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDocumentoInput) =>
      api.post<Documento>(`${basePath(workspaceId!)}/`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentos', workspaceId] });
    },
  });
}

export function useDeleteDocumento(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`${basePath(workspaceId!)}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentos', workspaceId] });
    },
  });
}
